import { createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import CompleteProfileLayout from '@/features/auth/signup/components/layouts/CompleteProfileLayout.tsx';
import CompleteUserDetailsPage from '@/features/auth/signup/pages/CompleteUserDetailsPage.tsx';
import CompleteCompanyDataPage from '@/features/auth/signup/pages/CompleteCompanyDataPage.tsx';
import { authRoute, queryClient } from '@/App.tsx';
import { auth } from '@/lib/firebase';
import { getMeQueryOptions } from '@/features/dashboard/_root/hooks/useMe.ts';
import { resolveEntryTarget } from '@/features/dashboard/_root/access/entryTarget.ts';
import { readStoredCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';
import { MyAccessDto } from '@/features/dashboard/_root/types/MyAccessDto.ts';
import { userApiQueryKeys } from '@/features/dashboard/_root/api/userApi.queryKeys.ts';

const searchSchema = z.object({
  verificationSuccess: z.boolean().optional(),
  newCompany: z.boolean().optional(),
});

export const completeProfileRoute = createRoute({
  getParentRoute: () => authRoute,
  path: '/complete-profile',
  component: CompleteProfileLayout,
  validateSearch: searchSchema,
  beforeLoad: async ({ search }) => {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) {
      if (search.verificationSuccess) {
        throw redirect({
          to: '/auth/login',
          search: { emailVerified: true },
        });
      }
      return;
    }
    if (search.verificationSuccess) {
      try {
        await user.reload();
        await user.getIdToken(true);
      } catch (e) {
        console.error('Failed to refresh ID token after verification', e);
      }
      queryClient.removeQueries({ queryKey: userApiQueryKeys.me() });
    }
    if (!user.emailVerified || search.newCompany) return;
    // Po weryfikacji z maila backend zawsze kieruje tutaj — kto ma firmę albo zaproszenie, nie zakłada nowej.
    let me: MyAccessDto;
    try {
      me = await queryClient.ensureQueryData(getMeQueryOptions(user));
    } catch (e) {
      // Nowy użytkownik musi móc dokończyć profil nawet przy awarii /access — formularz jest ważniejszy niż przekierowanie.
      console.error('Failed to load accesses before complete profile', e);
      return;
    }
    const target = resolveEntryTarget(me, readStoredCompanyId());
    if (target === 'workspaces') throw redirect({ to: '/auth/workspaces' });
    if (target !== 'complete-profile') throw redirect({ to: '/app' });
  },
});

export const completeUserDetailsIndexRoute = createRoute({
  getParentRoute: () => completeProfileRoute,
  path: '/',
  component: CompleteUserDetailsPage,
});

export const completeUserDetailsRoute = createRoute({
  getParentRoute: () => completeProfileRoute,
  path: '/user',
  component: CompleteUserDetailsPage,
});

export const completeCompanyDataRoute = createRoute({
  getParentRoute: () => completeProfileRoute,
  path: '/company',
  component: CompleteCompanyDataPage,
});
